import { BPFormProvider, Input, Select } from '@bpba/form-components';
import { Button, HStack, Span, VStack } from '@bpba/ui-components';
import { useForm } from 'react-hook-form';
import { UrlsFront } from '../constants/urls';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ICBU } from '../types/ICBU';
import { FixedTermNewRules } from './FixedTermNewRules';
import { IFixedTermLegal } from '../types/IFixedTermLegal';
import { PanelDivisor } from '../components/shared/PanelDivisor';
import { SubTitulo } from '../components/shared/SubTitulo';
import { Titulo } from '../components/shared/Titulo';
import { StyledBox } from '../components/shared/StyledBox';
import { InfoTooltip } from '../components/shared/InfoTooltip';
import { PEP_INFO, SOI_INFO, FATCA_INFO, LOGIN_DEFAULT_ERROR_MESSAGE, DEBIN_INFO_TEXT, MENU_ITEM_NEW, ALIAS_CHECK_ERROR } from '../constants/data';
import { TimeRestrictionMessage } from './TimeRestrictionMessage';
import { useTimeRestrictions } from './useTimeRestriction';
import { Spinner } from '../components/shared/Spinner';
import { useCBU } from './useCBU';
import { useErrorMessage } from '../components/shared/useErrorMessage';
import { axiosMessageExtractor } from '../config/axiosMessageExtractor';
import { ErrorMessageContainer } from '../components/shared/ErrorMessageContainer';
import { useMenuContext } from '../components/useMenuContext';
import { CUENTA_ORIGEN_ID, ORIGIN_FORM_KEY, useCBUorigin } from './useCBUorigin';
import { FaInfoCircle } from 'react-icons/fa';

const TOOLTIP_POSITION_LEFT = [0];
const TOOLTIP_MARGIN_BOTTOM = 18;

const ALIAS_INPUT_KEY = 'alias';

export const FixedTermNew = () => {
  const methods = useForm<ICBU & IFixedTermLegal>();
  const [requestErrorMessage, setRequestErrorMessage, requestError, clearRequestErrorMessage] = useErrorMessage(axiosMessageExtractor, LOGIN_DEFAULT_ERROR_MESSAGE);
  const [cbus, loadingCBU, loadingAliasCheck, hasCbu, aliasConversion] = useCBU(setRequestErrorMessage);
  const [origenesCbu, selectOrigin, formCbuMethods] = useCBUorigin(hasCbu);
  const { state } = useLocation();
  const navigate = useNavigate();
  const { setOption } = useMenuContext();
  const [timeEnabled, loadingTimeEnabled] = useTimeRestrictions(setRequestErrorMessage);

  const loading = loadingCBU || loadingTimeEnabled || loadingAliasCheck;

  useEffect(() => {
    clearRequestErrorMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setOption(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (data: ICBU & IFixedTermLegal) => {
    let cbu: string | undefined = undefined;
    let alias: string | undefined = undefined;

    switch (selectOrigin) {
      case CUENTA_ORIGEN_ID.ALIAS_NUEVO: {
        alias = data.alias;
        break;
      }
      case CUENTA_ORIGEN_ID.CBU_EXISTENTE: {
        cbu = data?.cbuOption?.id;
        break;
      }
      case CUENTA_ORIGEN_ID.CBU_NUEVO: {
        cbu = data?.cbu;
        break;
      }
    }

    const payload = {
      state: {
        ...state,
        cbu: cbu,
        alias: alias,
        IsPep: !data.NotPep,
        IsSoi: !data.NotSoi,
        IsFatca: !data.NotFatca,
      },
    };

    if (alias) {
      try {
        await aliasConversion(alias);
      } catch (error: any) {
        methods.setError(ALIAS_INPUT_KEY, { type: 'server', message: ALIAS_CHECK_ERROR });
        return;
      }
    }

    navigate(UrlsFront.FixedTermAccept, payload);
  };

  const onCancel = () => {
    navigate(UrlsFront.FixedTerms);
  };

  const onVolver = () => {
    navigate(UrlsFront.FixedTermSimulation);
  };

  return (
    <VStack>
      <Titulo>{MENU_ITEM_NEW}</Titulo>
      <PanelDivisor flexDirection={'column'}>
        <SubTitulo>Alta plazo fijo</SubTitulo>

        {/**Caso de carga de datos */}
        {loading && <Spinner width={40} my={'48px'} />}

        {/**Caso de error */}
        <ErrorMessageContainer show={!loading && requestError} errorMessage={requestErrorMessage} />
        <StyledBox display={!loading && requestError ? 'flex' : 'none'} justifyContent={'end'} mt={'24px'}>
          <Button type="button" variant="transparent" onClick={onCancel} maxWidth={['100px', '200px']} mx={'0px !important'}>
            Volver
          </Button>
        </StyledBox>

        {/**Caso fuera de horario*/}
        <TimeRestrictionMessage show={!loading && !requestError && !timeEnabled} onCancel={onCancel} />

        {/**Caso de formulario */}
        <BPFormProvider methods={methods} rules={FixedTermNewRules}>
          <VStack as="form" onSubmit={methods.handleSubmit(onSubmit)} display={!loading && !requestError && timeEnabled ? 'flex' : 'none'} noValidate>
            <StyledBox gap={'8px'} flexDirection={'row'} alignItems={'center'}>
              <FaInfoCircle />
              <Span fontSize={'12px'}>{DEBIN_INFO_TEXT}</Span>
            </StyledBox>
            <StyledBox flexDirection={['column', 'row']}>
              <Select
                width={['100%', '50%']}
                label="Origen CBU"
                name={ORIGIN_FORM_KEY}
                placeholder="Seleccioná un origen de CBU"
                options={origenesCbu}
                control={formCbuMethods.control}
              />

              {selectOrigin === CUENTA_ORIGEN_ID.CBU_EXISTENTE && (
                <Select
                  width={['100%', '50%']}
                  label="Seleccioná un CBU"
                  name="cbuOption"
                  control={methods.control}
                  placeholder="Tus CBU / Alias"
                  options={cbus}
                  defaultValue="null"
                />
              )}

              {selectOrigin === CUENTA_ORIGEN_ID.CBU_NUEVO && (
                <Input name="cbu" control={methods.control} label="Ingresá un nuevo CBU" width={['100%', '50%']} placeholder="Nuevo CBU" type="number" />
              )}

              {selectOrigin === CUENTA_ORIGEN_ID.ALIAS_NUEVO && (
                <Input name={ALIAS_INPUT_KEY} control={methods.control} label="Ingresá un nuevo alias" width={['100%', '50%']} placeholder="Nuevo alias" />
              )}
            </StyledBox>
            <StyledBox flexDirection={['column']} mt={'24px'} role="group" width={'100%'}>
              {/**TODO: Los iconos deberían pasarse como righIcon del Input, no dentro de un HStack. Cambiar cuando se actualice librería de formularios */}
              <HStack alignItems="center">
                <Span maxWidth={['250px']} width={'100% !important'}>
                  <Input
                    name="NotPep"
                    type="checkbox"
                    control={methods.control}
                    label="Declaro que no soy P.E.P."
                    rightIcon={InfoTooltip(PEP_INFO)}
                    containerStyle={{ width: 'fit-content' }}
                    aria-label={PEP_INFO}
                  />
                </Span>
                {InfoTooltip(PEP_INFO, TOOLTIP_POSITION_LEFT, TOOLTIP_MARGIN_BOTTOM)?.({})}
              </HStack>
              <HStack alignItems="center">
                <Span maxWidth={['250px']} width={'100% !important'}>
                  <Input
                    name="NotSoi"
                    type="checkbox"
                    control={methods.control}
                    label="Declaro que no soy S.O.I."
                    rightIcon={InfoTooltip(SOI_INFO)}
                    containerStyle={{ width: 'fit-content' }}
                    aria-label={SOI_INFO}
                  />
                </Span>
                {InfoTooltip(SOI_INFO, TOOLTIP_POSITION_LEFT, TOOLTIP_MARGIN_BOTTOM)?.({})}
              </HStack>
              <HStack alignItems="center">
                <Span maxWidth={['250px']} width={'100% !important'}>
                  <Input
                    name="NotFatca"
                    type="checkbox"
                    control={methods.control}
                    label="Declaro que no soy F.A.T.C.A."
                    rightIcon={InfoTooltip(FATCA_INFO)}
                    containerStyle={{ width: 'fit-content' }}
                    aria-label={FATCA_INFO}
                  />
                </Span>
                {InfoTooltip(FATCA_INFO, TOOLTIP_POSITION_LEFT, TOOLTIP_MARGIN_BOTTOM)?.({})}
              </HStack>
            </StyledBox>
            <StyledBox justifyContent={'end'} mt={'24px'} gap="8px" flexDirection={['column', 'row']}>
              <Button type="button" variant="transparent" onClick={onCancel} width={['100%', '200px']} m={'0px !important'}>
                Cancelar
              </Button>{' '}
              <Button type="button" variant="transparent" onClick={onVolver} width={['100%', '200px']} m={'0px !important'}>
                Volver
              </Button>
              <Button disabled={loading} type="submit" variant="secondary" width={['100%', '200px']} m={'0px !important'}>
                Confirmar
              </Button>
            </StyledBox>
          </VStack>
        </BPFormProvider>
      </PanelDivisor>
    </VStack>
  );
};
